import { useCallback, useEffect, useRef, useState } from "react";
import { SpeechDetector } from "./speech-detector";

export type HandsFreePhase = "off" | "listening" | "hearing" | "writing" | "waiting" | "error";

interface Session {
  stream: MediaStream;
  context: AudioContext;
  analyser: AnalyserNode;
  samples: Uint8Array<ArrayBuffer>;
  detector: SpeechDetector;
  frame: number;
  recorder: MediaRecorder | null;
  chunks: Blob[];
  resumeAt: number;
  writing: boolean;
  /** The last turn sent, so an echo of the same words is never sent twice. */
  lastSent: string;
}

/**
 * Speech recognition invents text when it only hears silence or noise; these are its
 * well-known inventions, in English and Dutch. They are never a real answer to Iris.
 */
const INVENTED_SPEECH = [
  /ondertitel/i,
  /amara\.org/i,
  /bedankt voor het (kijken|luisteren)/i,
  /thanks? (you )?for watching/i,
  /subtitles? by/i,
  // A lone "you" or a filler sound; "ok", "yes" and "thanks" stay, they can be real answers.
  /^(you|hmm+|uh+|eh+|um+)[.!?]*$/i,
];

export function isRealSpeech(text: string): boolean {
  const words = text.trim();
  if (!/[\p{L}\p{N}]/u.test(words)) return false;
  return !INVENTED_SPEECH.some((pattern) => pattern.test(words));
}

/** After Iris stops talking, the room needs a moment before it is heard again. */
const RESUME_DELAY_MS = 450;

/**
 * A normal conversation with Iris: the microphone stays open, a turn ends when the maker is
 * quiet for a moment, and what they said is sent at once. While Iris thinks or speaks
 * nothing is recorded, so she never hears her own voice through the speakers.
 */
export function useHandsFree({
  transcribe,
  onText,
  paused,
  level,
}: {
  transcribe?: (recording: Blob) => Promise<string>;
  onText: (text: string) => void;
  /** Iris is thinking or speaking. */
  paused: boolean;
  level: { current: number };
}) {
  const [phase, setPhase] = useState<HandsFreePhase>("off");
  const [message, setMessage] = useState("");
  const session = useRef<Session | null>(null);
  const pausedRef = useRef(paused);
  const handlers = useRef({ transcribe, onText });
  handlers.current = { transcribe, onText };
  const supported =
    !!transcribe &&
    typeof window !== "undefined" &&
    "MediaRecorder" in window &&
    !!window.AudioContext &&
    !!navigator.mediaDevices?.getUserMedia;

  const discardRecording = (current: Session) => {
    const recorder = current.recorder;
    current.recorder = null;
    current.chunks = [];
    if (recorder && recorder.state !== "inactive") {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.stop();
    }
  };

  const finishTurn = (current: Session) => {
    const recorder = current.recorder;
    if (!recorder) return;
    current.recorder = null;
    current.writing = true;
    setPhase("writing");
    recorder.onstop = async () => {
      const recording = new Blob(current.chunks, { type: recorder.mimeType || "audio/webm" });
      current.chunks = [];
      try {
        const text = recording.size ? (await handlers.current.transcribe!(recording)).trim() : "";
        if (session.current !== current) return;
        if (isRealSpeech(text) && text.toLowerCase() !== current.lastSent) {
          current.lastSent = text.toLowerCase();
          handlers.current.onText(text);
        }
        setMessage("");
      } catch (failure) {
        if (session.current === current)
          setMessage(failure instanceof Error ? failure.message : "Iris could not hear that. Try again.");
      } finally {
        current.writing = false;
        current.resumeAt = performance.now() + RESUME_DELAY_MS;
        if (session.current === current) setPhase("waiting");
      }
    };
    recorder.stop();
  };

  const listen = (current: Session) => {
    const recorder = new MediaRecorder(current.stream);
    current.chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size) current.chunks.push(event.data);
    };
    recorder.start();
    current.recorder = recorder;
    // The room was measured when the conversation began; a quick answer is not background.
    current.detector.rearm();
    setPhase("listening");
  };

  const tick = () => {
    const current = session.current;
    if (!current) return;
    current.frame = requestAnimationFrame(tick);
    const now = performance.now();
    if (pausedRef.current || current.writing) {
      if (current.recorder) discardRecording(current);
      current.resumeAt = now + RESUME_DELAY_MS;
      return;
    }
    if (now < current.resumeAt) return;
    if (!current.recorder) listen(current);
    current.analyser.getByteTimeDomainData(current.samples);
    let sum = 0;
    for (const sample of current.samples) sum += ((sample - 128) / 128) ** 2;
    const loudness = Math.min(1, Math.sqrt(sum / current.samples.length) * 4.5);
    level.current = loudness;
    const event = current.detector.feed(loudness, now);
    if (event === "start") setPhase("hearing");
    if (event === "end") finishTurn(current);
    if (event === "noise") {
      // A tick, a breath or an echo: nothing is sent, Iris simply keeps listening.
      discardRecording(current);
      setPhase("listening");
    }
  };

  const stop = useCallback(() => {
    const current = session.current;
    session.current = null;
    if (!current) return;
    cancelAnimationFrame(current.frame);
    discardRecording(current);
    current.stream.getTracks().forEach((track) => track.stop());
    void current.context.close().catch(() => {});
    level.current = 0;
    setPhase("off");
  }, [level]);

  const start = useCallback(async () => {
    if (session.current || !supported) return;
    setMessage("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      session.current = {
        stream,
        context,
        analyser,
        samples: new Uint8Array(analyser.fftSize),
        detector: new SpeechDetector(),
        frame: 0,
        recorder: null,
        chunks: [],
        resumeAt: 0,
        writing: false,
        lastSent: "",
      };
      setPhase("waiting");
      session.current.frame = requestAnimationFrame(tick);
    } catch {
      setMessage("The microphone is not available. Allow it in the browser, or type instead.");
      setPhase("error");
    }
    // tick reads everything through refs, so it never goes stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);
  useEffect(() => stop, [stop]);

  return { supported, phase, message, start, stop, active: phase !== "off" && phase !== "error" };
}
